import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * `React.lazy` for a page chunk, plus one recovery: a tab opened before a
 * deploy reloads itself when the chunk it asks for is gone.
 *
 * Why it happens: chunk names are content-hashed, and a deploy removes the old
 * ones. A tab that loaded the old `index-*.js` still asks for the old names on
 * its next navigation, and the load fails. Before this, the page crashed into
 * the error boundary and filed a bug report (preview, 2026-09-25, `ebe0f87d`).
 * A reload fetches the new `index.html` and the new chunk names, so it is the
 * whole fix.
 *
 * Why not Vite's `vite:preloadError` event: cancelling it makes the import
 * RESOLVE with `undefined`, so the `.then(m => m.Page)` after it throws a
 * TypeError instead — still a crash, still a bug report. Not cancelling it
 * rethrows. Catching here, where the page loads, is the only place that can
 * hold the Suspense fallback on screen until the reload lands.
 */

/** sessionStorage key: when this tab last reloaded for a stale chunk. */
export const STALE_BUILD_RELOAD_KEY = 'stale-build-reload-at';

/**
 * A second failure inside this window is not a stale tab — the reload already
 * fetched the current build — so it surfaces as the error it is, instead of
 * reloading forever.
 */
export const STALE_BUILD_RELOAD_GUARD_MS = 60_000;

export interface StaleBuildEnv {
  now:     () => number;
  online:  () => boolean;
  /** May throw: storage is blocked in some private windows. */
  storage: () => Pick<Storage, 'getItem' | 'setItem'>;
  reload:  () => void;
}

/**
 * Reload once for a failed chunk load. Returns true when it reloaded.
 *
 * Refuses when offline (a reload shows the browser's offline page, which
 * explains less than the app's own error), and when the tab cannot record the
 * reload — without the mark, nothing stops the next failure reloading again.
 */
export function reloadForStaleBuild(env: StaleBuildEnv): boolean {
  if (!env.online()) return false;
  try {
    const storage = env.storage();
    const now = env.now();
    const last = Number(storage.getItem(STALE_BUILD_RELOAD_KEY));
    if (now - last < STALE_BUILD_RELOAD_GUARD_MS) return false;
    storage.setItem(STALE_BUILD_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  env.reload();
  return true;
}

/**
 * Wraps a page loader. On failure it reloads once and returns a promise that
 * never settles, so Suspense keeps showing its fallback until the new page
 * replaces this one. When it does not reload, the original error propagates.
 */
export function withStaleBuildReload<M>(load: () => Promise<M>, env: StaleBuildEnv): () => Promise<M> {
  return () => load().catch((err: unknown) => {
    if (reloadForStaleBuild(env)) return new Promise<M>(() => {});
    throw err;
  });
}

const browserEnv: StaleBuildEnv = {
  now:     () => Date.now(),
  online:  () => navigator.onLine,
  storage: () => window.sessionStorage,
  reload:  () => window.location.reload(),
};

// `any` matches React.lazy's own constraint — a page's props are its own.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
  env: StaleBuildEnv = browserEnv,
): LazyExoticComponent<T> {
  return lazy(withStaleBuildReload(load, env));
}
